import type {
  CreditMemo,
  CreditMemoBalanceRow,
  CreditMemoLine,
  CreditMemoStatus,
} from "../../../reducers/creditMemo";

/**
 * Pure helpers for the Available Credit Memo screen, kept apart from the
 * components so the wording an operator acts on can be tested.
 */

type Tone = "info" | "success" | "warning" | "error";

const moneyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

/**
 * @param {number | null | undefined} amount dollars
 * @return {string}
 */
export const formatMoney = (amount: number | null | undefined): string =>
  amount === null || amount === undefined ? "-" : moneyFormatter.format(amount);

/**
 * @param {string | null | undefined} value ISO date
 * @return {string} YYYY-MM-DD, or "-"
 */
export const formatDay = (value: string | null | undefined): string =>
  value ? value.slice(0, 10) : "-";

export const STATUS_LABELS: Record<CreditMemoStatus, string> = {
  Pending: "Awaiting approval",
  AwaitingBankConfirmation: "Awaiting bank confirmation",
  AwaitingPayout: "Ready to pay out",
  Completed: "Completed",
  Cancelled: "Cancelled",
  Voided: "Voided",
};

export const STATUS_TONES: Record<CreditMemoStatus, Tone> = {
  Pending: "warning",
  AwaitingBankConfirmation: "info",
  AwaitingPayout: "info",
  Completed: "success",
  Cancelled: "error",
  Voided: "error",
};

/**
 * Whether the refunded tax still has to be moved at DC Bank, from the Aya
 * Master wallet to the business wallet. Tax refunded from the business
 * tax bucket is in the business wallet already.
 *
 * @param {CreditMemo} memo
 * @return {boolean}
 */
export const needsTaxTransfer = (memo: CreditMemo): boolean =>
  memo.taxAmount > 0 &&
  memo.taxSource !== "TaxBucket" &&
  !memo.taxTransferDoneAt &&
  (memo.status === "AwaitingBankConfirmation" ||
    memo.status === "AwaitingPayout");

/**
 * The one thing the super admin has to do next, in words.
 *
 * @param {CreditMemo} memo
 * @return {string}
 */
export const nextStep = (memo: CreditMemo): string => {
  switch (memo.status) {
    case "Pending":
      return "Check the balances and approve. Approving builds the PDF and emails the plan admin. The money stays in the business wallet Return balance and tax bucket, where nothing else can spend it, until it is paid out.";
    case "AwaitingBankConfirmation":
      return needsTaxTransfer(memo)
        ? "Move the tax at DC Bank, and record the bank account the plan admin confirms by email."
        : "Record the bank account the plan admin confirms by email.";
    case "AwaitingPayout":
      if (needsTaxTransfer(memo)) {
        return "Move the tax at DC Bank first, then send the payout.";
      }
      if (memo.payoutStatus === "NeedsCheck" || memo.payoutStatus === "Sending") {
        return "The payout was sent to DC Bank and its outcome is not known. Check it below before doing anything else.";
      }
      return "Send the payout: the total moves from the business wallet to the DC Bank account you choose, and finance pays the business from there.";
    case "Completed":
      return "Nothing left to do.";
    case "Cancelled":
      return "This credit memo was cancelled.";
    case "Voided":
      return "This credit memo was voided. Its transactions come back on a new memo at the next detection.";
    default:
      return "";
  }
};

/**
 * @param {CreditMemoLine} line
 * @return {string} HSA, WSA or Flex, as the PDF prints it
 */
export const productLabel = (line: CreditMemoLine): string => {
  if (line.transactionCode === "L-CRE") {
    return "Flex";
  }
  return line.productName === "Prepaid HSA" ? "HSA" : "WSA";
};

const TRANSACTION_LABELS: Record<string, string> = {
  "T-BDV": "Terminated",
  "E-BDV": "Renewal / grace expired",
  "L-CRE": "Flex to WSA (tax only)",
  "R-CON": "Contribution returned, employee terminated",
};

/**
 * @param {CreditMemoLine} line
 * @return {string}
 */
export const transactionLabel = (line: CreditMemoLine): string =>
  TRANSACTION_LABELS[line.transactionCode] || line.transactionCode;

/**
 * One sentence per mismatched balance, for the pop-up.
 *
 * @param {CreditMemoBalanceRow} row
 * @return {string}
 */
export const describeMismatch = (row: CreditMemoBalanceRow): string => {
  const who =
    row.scope === "wallet"
      ? `Business wallet (${row.entityName})`
      : `User wallet of ${row.entityName}`;
  const bank =
    row.dcBankBalance === null
      ? "DC Bank has no balance on file"
      : `DC Bank holds ${formatMoney(row.dcBankBalance)}`;
  return `${who}: Aya expects ${formatMoney(row.expectedDcBank)} at DC Bank, ${bank}.`;
};

export interface BankForm {
  useAccountOnFile: boolean;
  institution: string;
  transit: string;
  accountNumber: string;
  note: string;
}

/**
 * Checks the bank account typed in before it is sent, with the rules the
 * service applies.
 *
 * @param {BankForm} form
 * @return {string} the problem, or empty when the form can be sent
 */
export const validateBankForm = (form: BankForm): string => {
  if (form.useAccountOnFile) {
    return "";
  }
  if (!/^[0-9]{3}$/.test(form.institution.trim())) {
    return "The institution number has 3 digits.";
  }
  if (!/^[0-9]{5}$/.test(form.transit.trim())) {
    return "The transit number has 5 digits.";
  }
  if (!/^[0-9]{5,12}$/.test(form.accountNumber.trim())) {
    return "The account number has 5 to 12 digits.";
  }
  return "";
};

/** One cell of the credit memo Excel export, as the invoice service lays it out. */
export interface CreditMemoSheetCell {
  text?: string;
  // Dollars.
  money?: number;
  bold?: boolean;
}

/** The credit memo laid out like its PDF, row by row. */
export interface CreditMemoSpreadsheet {
  sheetName: string;
  fileName: string;
  rows: CreditMemoSheetCell[][];
}

/** A cell in the shape the xlsx library writes. */
export interface WorkbookCell {
  t: "s" | "n";
  v: string | number;
  z?: string;
}

const MONEY_FORMAT = '"$"#,##0.00';

/**
 * Turns the service's rows into xlsx cells: money as numbers with a dollar
 * format, so the totals can be added up in Excel, and text as text.
 *
 * @param {CreditMemoSpreadsheet} sheet
 * @return {WorkbookCell[][]}
 */
export const creditMemoWorkbookRows = (sheet: CreditMemoSpreadsheet): WorkbookCell[][] =>
  sheet.rows.map((row) =>
    (row || []).map((cell): WorkbookCell => {
      if (cell && typeof cell.money === "number") {
        return { t: "n", v: cell.money, z: MONEY_FORMAT };
      }
      return { t: "s", v: cell?.text ?? "" };
    })
  );

/**
 * The width of each column, in characters, from its longest value. The
 * address and note lines in the first column are left out so it stays narrow.
 *
 * @param {WorkbookCell[][]} rows
 * @return {{ wch: number }[]}
 */
export const creditMemoColumnWidths = (rows: WorkbookCell[][]): { wch: number }[] => {
  const widths: number[] = [];
  rows.forEach((row) => {
    if (row.length < 2) return;
    row.forEach((cell, index) => {
      const length = cell.t === "n" ? 14 : String(cell.v).length + 2;
      widths[index] = Math.min(Math.max(widths[index] || 10, length), 45);
    });
  });
  return widths.map((wch) => ({ wch: wch || 10 }));
};

/**
 * @param {string} base64
 * @param {string} contentType
 * @return {Blob}
 */
export const base64ToBlob = (base64: string, contentType: string): Blob => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: contentType });
};
